/*
 * PROD-7828 D110 — pure adapters for month close/reopen runs: goserver MonthRun payloads -> view models.
 * The run panel renders only what these return; adapters.test.js replays the goldens in __fixtures__/
 * and deep-equals the key lists below against month_run_enums.json, so a wire drift fails there.
 */
import { api_error, fmt_count, month_label as month_name, num } from '../../format';
import {
  CLOSE_SETTLE_NOTE,
  OUTCOME_NOT_CONFIRMED_LABEL,
  close_result_view,
  month_write_failure_kind,
  readiness_view,
  reopen_view
} from '../adapters';
import {
  MAX_RUN_TEACHERS,
  RUN_KIND_CLOSE,
  RUN_KIND_REOPEN,
  is_run_active,
  item_blockers
} from './params';

const text = (v, fallback = '') => {
  const s = v === null || v === undefined ? '' : String(v);
  return s === '' ? fallback : s;
};

const list_of = (v) => (Array.isArray(v) ? v : []);

// ---- the wire key lists, deep-equalled against month_run_enums.json ----

export const RUN_KEYS = [
  'run_uuid',
  'kind',
  'pay_month',
  'status',
  'paused_reason',
  'selection',
  'started_by',
  'started_at_ist',
  'finished_at_ist',
  'client_request_id',
  'reason',
  'override_used',
  'counts',
  'headline',
  'current_teacher',
  'cancel_requested',
  'can_cancel',
  'can_resume',
  'resume_takes_over',
  'taken_over',
  'items',
  'items_total',
  'readiness'
];

export const RUN_SUMMARY_KEYS = [
  'run_uuid',
  'kind',
  'pay_month',
  'status',
  'paused_reason',
  'started_by',
  'started_at_ist',
  'finished_at_ist',
  'client_request_id',
  'override_used',
  'counts',
  'headline'
];

export const RUN_ITEM_KEYS = [
  'seq',
  'status',
  'attempts',
  'teacher_uuid',
  'teacher_name',
  'close_result',
  'reopen_result',
  'message',
  'retryable'
];

export const RUN_COUNTS_KEYS = [
  'total',
  'pending',
  'running',
  'closed',
  'reopened',
  'already_done',
  'refused',
  'failed',
  'unknown',
  'not_attempted',
  'needs_attention',
  'retryable'
];

export const RUNS_RESPONSE_KEYS = ['active', 'rows', 'total_count'];
export const RUN_CREATE_RESPONSE_KEYS = ['run', 'replayed', 'requires_override', 'precheck'];
export const RUN_SELECTION_KEYS = ['mode', 'state', 'q', 'excluded_no_month_row'];
export const RUN_ERROR_KEYS = [
  'status',
  'reason',
  'message',
  'error_code',
  'active_run_uuid',
  'run_uuid',
  'matched_count'
];

export const RUN_ERROR_CODES = [
  'BAD_BODY',
  'BAD_KIND',
  'BAD_PAY_MONTH',
  'REASON_REQUIRED',
  'REASON_TOO_LONG',
  'INVALID_CLIENT_REQUEST_ID',
  'BAD_SELECTION',
  'BAD_TEACHER_UUID',
  'BAD_STATE',
  'BAD_QUERY',
  'TOO_MANY_TEACHERS',
  'NO_TEACHERS',
  'TEACHER_NOT_FOUND',
  'SELECTION_CHANGED',
  'BAD_BUCKET',
  'BAD_PAGINATION',
  'RUN_ALREADY_ACTIVE',
  'IDEMPOTENCY_MISMATCH',
  'RUN_NOT_FOUND',
  'RUN_NOT_YOURS',
  'RUN_FINISHED',
  'RUN_NOT_PAUSED',
  'STARTER_NO_ACCESS',
  'RESUME_TAKEOVER_CHANGED',
  'CLOSE_DISABLED',
  'FORBIDDEN',
  'LOOKUP_FAILED'
];

// ---- labels and tones (presentation only) ----

const KIND_WORDS = {
  CLOSE: { noun: 'close', verb: 'close', doing: 'Closing', title: 'Close run' },
  REOPEN: { noun: 'reopen', verb: 'reopen', doing: 'Reopening', title: 'Reopen run' }
};
const kind_words = (kind) => KIND_WORDS[kind] || KIND_WORDS[RUN_KIND_CLOSE];

const PAUSED_LABELS = {
  SWITCH_OFF: 'Paused: month close switched off',
  FAILING: 'Paused: repeated failures',
  // Short enough for the pill and the banner on a phone; the panel body carries the full sentence.
  STARTER_NO_ACCESS: 'Paused: payment access lost'
};

export const PAUSED_SWITCH_OFF_TEXT =
  'Month close was switched off, so the run paused after the teacher in progress. It does not restart by itself: once the switch is back on, the person who started it or a super user presses Resume.';
export const PAUSED_FAILING_TEXT =
  'Five teachers in a row failed, so the run paused. This usually means a database or network problem rather than a problem with these teachers. Resume once it is fixed.';
// CD-274 amended: every teacher is closed (or reopened) in the acting person's name, so only a super user can take
// it over.
export const paused_no_access_text = (kind) =>
  `The person this run ${
    kind === RUN_KIND_REOPEN ? 'reopens' : 'closes'
  } under no longer has payment access, so it paused. A super user can resume it under their own name, or stop it.`;
export const PAUSED_STARTER_NO_ACCESS_TEXT = paused_no_access_text(RUN_KIND_CLOSE);
// Kuldeep's copy (2026-09-28), shown beside "Resume as you".
// The takeover confirm: how many teachers move to the viewer's name, and in which month.
export const takeover_confirm_text = (run) => {
  const left = num(run.counts.pending) + num(run.counts.running);
  const verb = run.kind === RUN_KIND_REOPEN ? 'reopened' : 'closed';
  return `Resume this run as you? The remaining ${fmt_count(left)} teacher${
    left === 1 ? '' : 's'
  } of ${run.month_label} will be ${verb} under your name, with the run's reason.`;
};

export const resume_takes_over_text = (kind) =>
  `Resume as you - the remaining teachers will be ${
    kind === RUN_KIND_REOPEN ? 'reopened' : 'closed'
  } under your name`;
export const RESUME_WAITS_FOR_SWITCH_TEXT =
  'Resume becomes available once month close is switched on again.';

const run_status_label = (status, paused_reason, stopping) => {
  if (stopping) return 'Stopping after the current teacher';
  if (status === 'PAUSED') return PAUSED_LABELS[paused_reason] || 'Paused';
  return (
    { QUEUED: 'Queued', RUNNING: 'Running', COMPLETED: 'Completed', CANCELLED: 'Stopped' }[
      status
    ] || status
  );
};

const run_status_tone = (status, paused_reason, stopping) => {
  if (stopping) return 'orange';
  if (status === 'PAUSED') return paused_reason === 'SWITCH_OFF' ? 'orange' : 'red';
  return (
    { QUEUED: 'gray', RUNNING: 'blue', COMPLETED: 'green', CANCELLED: 'gray' }[status] || 'gray'
  );
};

const ITEM_TONES = {
  PENDING: 'gray',
  RUNNING: 'blue',
  CLOSED: 'green',
  REOPENED: 'green',
  ALREADY_DONE: 'gray',
  REFUSED: 'red',
  FAILED: 'red',
  UNKNOWN: 'orange',
  NOT_ATTEMPTED: 'gray'
};

const item_status_label = (status, kind) => {
  if (status === 'ALREADY_DONE')
    return kind === RUN_KIND_REOPEN ? 'Already open' : 'Already closed';
  if (status === 'UNKNOWN') return OUTCOME_NOT_CONFIRMED_LABEL;
  return (
    {
      PENDING: 'Pending',
      RUNNING: 'In progress',
      CLOSED: 'Closed',
      REOPENED: 'Reopened',
      REFUSED: 'Refused',
      FAILED: 'Failed',
      NOT_ATTEMPTED: 'Not attempted'
    }[status] || status
  );
};

// The tier a close blocker points at, for the "View issues" drill-down (CD-104).
const BLOCKER_TIERS = { CLOSE_BLOCKED_ERRORS: 'ERRORS', CLOSE_BLOCKED_ISSUES: 'MUST_REVIEW' };

const issues_tier_of = (item) => {
  const hit = item_blockers(item).find((b) => BLOCKER_TIERS[b.code]);
  return hit ? BLOCKER_TIERS[hit.code] : '';
};

// ---- views ----

export const run_counts_view = (counts) => {
  const c = counts || {};
  return RUN_COUNTS_KEYS.reduce((acc, k) => ({ ...acc, [k]: num(c[k]) }), {});
};

export const BUCKET_LABELS = {
  NEEDS_ATTENTION: 'Needs attention',
  DONE: 'Done',
  PENDING: 'Pending',
  NOT_ATTEMPTED: 'Not attempted',
  ALL: 'All'
};

// monthRunBucketStatuses, counted: DONE = closed + reopened + already done, PENDING = pending + running.
export const bucket_count = (counts, bucket) => {
  const c = counts || {};
  switch (bucket) {
    case 'NEEDS_ATTENTION':
      return num(c.needs_attention);
    case 'DONE':
      return num(c.closed) + num(c.reopened) + num(c.already_done);
    case 'PENDING':
      return num(c.pending) + num(c.running);
    case 'NOT_ATTEMPTED':
      return num(c.not_attempted);
    default:
      return num(c.total);
  }
};

export const run_item_view = (item, kind) => {
  const it = item || {};
  const status = text(it.status);
  const close = it.close_result ? close_result_view(it.close_result) : null;
  const reopen = it.reopen_result ? reopen_view(it.reopen_result) : null;
  const lines = [];
  if (close) {
    close.blockers.forEach((b) => lines.push(b.line));
    close.grant_mismatch.forEach((g) => lines.push(`${g.line} — ${g.how_to_fix}`));
    if (close.grant_mismatch_unnamed_text) lines.push(close.grant_mismatch_unnamed_text);
  }
  if (reopen) {
    reopen.notes.forEach((n) => lines.push(n));
    reopen.blockers.forEach((b) => lines.push(b.line));
  }
  const message = text(it.message);
  if (message) lines.push(message);
  const facts = close ? [close.net_text, close.grant_text].filter(Boolean) : [];
  return {
    key: `${num(it.seq)}-${text(it.teacher_uuid, 'teacher')}`,
    seq: num(it.seq),
    status,
    status_label: item_status_label(status, kind),
    status_tone: ITEM_TONES[status] || 'gray',
    teacher_uuid: text(it.teacher_uuid),
    teacher_name: text(it.teacher_name, text(it.teacher_uuid, '—')),
    attempts: num(it.attempts),
    facts_text: facts.join(' · '),
    lines: lines.filter(Boolean),
    issues_tier: issues_tier_of(it)
  };
};

const started_text = (d) => {
  const by = text(d.started_by && d.started_by.name, text(d.started_by && d.started_by.uuid));
  const at = text(d.started_at_ist);
  return [by ? `Started by ${by}` : '', at ? `${at} IST` : ''].filter(Boolean).join(' · ');
};

const taken_over_text = (t) => {
  if (!t) return '';
  const by = text(t.by && t.by.name, text(t.by && t.by.uuid, 'a super user'));
  const at = text(t.at_ist);
  return `Taken over by ${by}${at ? ` on ${at} IST` : ''}`;
};

const cancel_text = (c) => {
  if (!c) return '';
  const by = text(c.by && c.by.name, text(c.by && c.by.uuid, 'someone'));
  const at = text(c.at_ist);
  const reason = text(c.reason);
  return `Stop requested by ${by}${at ? ` at ${at} IST` : ''}${
    reason ? `: ${reason}` : ''
  }. The teacher in progress finishes; the rest will not be attempted.`;
};

// CD-267/CD-269: a FILTER close skips teachers with no pay row; the server counts them (absent = 0).
const skipped_text = (selection) => {
  const n = num(selection && selection.excluded_no_month_row);
  return n > 0 ? `${fmt_count(n)} skipped: no pay row for this month` : '';
};

const PAUSE_TEXTS = {
  SWITCH_OFF: PAUSED_SWITCH_OFF_TEXT,
  FAILING: PAUSED_FAILING_TEXT
};

const pause_text = (status, paused_reason, kind) => {
  if (status !== 'PAUSED') return '';
  if (paused_reason === 'STARTER_NO_ACCESS') return paused_no_access_text(kind);
  return PAUSE_TEXTS[paused_reason] || PAUSED_SWITCH_OFF_TEXT;
};

// Why Resume is missing on a paused run: the switch (the server's can_resume folds it in) or the person.
const resume_hint = (d, starter) => {
  if (d.status !== 'PAUSED' || d.can_resume === true) return '';
  if (d.paused_reason === 'SWITCH_OFF') return RESUME_WAITS_FOR_SWITCH_TEXT;
  // Its pause text already says who can act and how.
  if (d.paused_reason === 'STARTER_NO_ACCESS') return '';
  return `Only ${starter || 'the person who started it'} or a super user can resume this run.`;
};

export const teacher_months = (n) => `${fmt_count(n)} teacher-month${num(n) === 1 ? '' : 's'}`;

const needs_attention_text = (n) =>
  `${fmt_count(n)} ${n === 1 ? 'teacher needs' : 'teachers need'} attention`;

// The polite announcement: the status only (a finish adds what needs a look), so a poll that only moves
// the counter says nothing (review R1).
const announce_text = (title, pay_month, status_label, terminal, counts) => {
  const head = `${title} for ${pay_month ? month_name(pay_month) : 'this month'}: ${status_label}`;
  return terminal && counts.needs_attention > 0
    ? `${head} · ${needs_attention_text(counts.needs_attention)}`
    : head;
};

export const run_view = (data) => {
  const d = data || {};
  const kind = d.kind === RUN_KIND_REOPEN ? RUN_KIND_REOPEN : RUN_KIND_CLOSE;
  const words = kind_words(kind);
  const status = text(d.status);
  const paused_reason = text(d.paused_reason);
  const active = is_run_active(status);
  const stopping = active && !!d.cancel_requested;
  const counts = run_counts_view(d.counts);
  const done = Math.max(counts.total - counts.pending - counts.running, 0);
  const starter = text(d.started_by && d.started_by.name);
  const items = list_of(d.items).map((it) => run_item_view(it, kind));
  const status_label = run_status_label(status, paused_reason, stopping);
  const current = d.current_teacher
    ? text(d.current_teacher.name, text(d.current_teacher.uuid))
    : '';
  return {
    run_uuid: text(d.run_uuid),
    kind,
    title: words.title,
    pay_month: text(d.pay_month),
    month_label: d.pay_month ? month_name(d.pay_month) : '',
    status,
    active,
    terminal: !active && status !== '',
    paused_reason,
    stopping,
    status_label,
    status_tone: run_status_tone(status, paused_reason, stopping),
    headline: text(d.headline),
    progress_text: `${fmt_count(done)} of ${fmt_count(counts.total)} done`,
    announce_text: announce_text(words.title, d.pay_month, status_label, !active, counts),
    started_text: started_text(d),
    taken_over_text: taken_over_text(d.taken_over),
    finished_text: d.finished_at_ist ? `Finished ${d.finished_at_ist} IST` : '',
    current_text: current && status === 'RUNNING' ? `${words.doing} ${current}` : '',
    cancel_text: cancel_text(active ? d.cancel_requested : null),
    pause_text: pause_text(status, paused_reason, kind),
    resume_hint: resume_hint(d, starter),
    skipped_text: skipped_text(d.selection),
    reason: text(d.reason),
    override_used: d.override_used === true,
    client_request_id: text(d.client_request_id),
    counts,
    done,
    // A cancel already asked for is not offered twice.
    can_cancel: d.can_cancel === true && !stopping,
    can_resume: d.can_resume === true,
    // The server's rule: this viewer's Resume hands the run to them.
    resume_takes_over: d.can_resume === true && d.resume_takes_over === true,
    resume_takes_over_text:
      d.can_resume === true && d.resume_takes_over === true ? resume_takes_over_text(kind) : '',
    items,
    // B8/CD-269: the whole run's retryable count, so a teacher on a later page still offers the retry.
    can_retry: !active && status !== '' && counts.retryable > 0,
    items_total: num(d.items_total),
    readiness: d.readiness ? readiness_view(d.readiness) : null
  };
};

export const run_summary_view = (data) => (data ? run_view({ ...data, items: [] }) : null);

export const runs_view = (data) => {
  const d = data || {};
  return {
    active: d.active ? run_summary_view(d.active) : null,
    rows: list_of(d.rows).map(run_summary_view),
    total_count: num(d.total_count)
  };
};

// CD-39: after an unanswered create, the run this request started is found by its client_request_id.
export const own_run_uuid = (runs_data, client_request_id) => {
  if (!client_request_id) return '';
  const d = runs_data || {};
  const all = [d.active].concat(list_of(d.rows)).filter(Boolean);
  const hit = all.find((r) => r.client_request_id === client_request_id);
  return hit ? text(hit.run_uuid) : '';
};

// ---- create / cancel / resume outcomes ----

/*
 * How a refused or unanswered run request ended. 'dropped' (no answer, or an uncoded 5xx) may have
 * started a run; every coded refusal started nothing. The refusal's own keys ride along.
 */
export const run_request_failure = (e) => {
  const { code, message } = api_error(e);
  const body = (e && e.response && e.response.data) || {};
  const base = {
    code,
    message,
    matched_count: body.matched_count === undefined ? null : num(body.matched_count),
    run_uuid: text(body.active_run_uuid, text(body.run_uuid))
  };
  const kind = month_write_failure_kind(e);
  if (kind !== 'refused') return { ...base, kind };
  const by_code = {
    SELECTION_CHANGED: 'selection_changed',
    RUN_ALREADY_ACTIVE: 'already_active',
    IDEMPOTENCY_MISMATCH: 'idempotency_mismatch'
  };
  return { ...base, kind: by_code[code] || 'refused' };
};

export const RUN_CHECKING_TEXT = 'The run may have started. Checking…';
export const RUN_NOT_STARTED_TEXT =
  'The connection dropped and no run was found for this request, so nothing started. Trying again is safe: it cannot start a second run.';
export const RUN_CHECK_FAILED_TEXT =
  'The connection dropped and the check for the run failed too. Trying again is safe: the same request cannot start a second run.';

// SELECTION_CHANGED carries the count the filter matches now; the confirm is asked again with it.
export const selection_changed_text = (failure, expected) =>
  failure.message ||
  `The list now matches ${fmt_count(failure.matched_count)} teachers, not the ${fmt_count(
    expected
  )} you confirmed. Nothing was started.`;

// The count a SELECTION_CHANGED confirm may be asked again with; 0 when there is nothing one run can
// take (no teacher left, or more than a run's cap), so the dialog only says why.
export const selection_reask_count = (failure) => {
  const n = num(failure && failure.matched_count);
  return n > 0 && n <= MAX_RUN_TEACHERS ? n : 0;
};

export const run_confirm_text = ({ kind, count, month_label, state }) => {
  if (kind === RUN_KIND_REOPEN) {
    return `Reopen ${teacher_months(count)} of ${month_label}? Each one reverses its Free Pass grant and re-checks the later months, so money moves back.`;
  }
  const blocked =
    state === 'BLOCKED'
      ? ' A teacher whose blocker cannot be overridden is refused and listed under Needs attention.'
      : '';
  return `Close ${teacher_months(count)} of ${month_label}? The list is fixed when the run starts: a teacher who becomes ready later needs a new run.${blocked}`;
};

// The Overview banner: "Closing June 2026: 240 / 700", plus what needs a look or why it waits.
export const run_banner_text = (run) => {
  if (!run || !run.active) return '';
  const doing = kind_words(run.kind).doing;
  const parts = [
    `${doing} ${run.month_label}: ${fmt_count(run.done)} / ${fmt_count(run.counts.total)}`
  ];
  if (run.counts.needs_attention > 0) {
    parts.push(
      `${fmt_count(run.counts.needs_attention)} ${run.counts.needs_attention === 1 ? 'needs' : 'need'} attention`
    );
  }
  if (run.status === 'PAUSED' || run.stopping) parts.push(run.status_label);
  return parts.join(' · ');
};

// While a run is active the month's Close/Reopen buttons are off (one active run per month).
export const run_busy_text = (run, label) =>
  run && run.active
    ? `A ${kind_words(run.kind).noun} run is in progress for ${label || run.month_label}.`
    : '';

export const REOPEN_SETTLE_NOTE =
  'The teacher list and month summary can take a moment to show this reopen. Refresh if a teacher you just reopened still shows as closed.';

// D74(a): the summary is a replica read; a run that may have written anything may lag in it.
export const run_settle_note = (run) => {
  if (!run) return '';
  const c = run.counts || {};
  if (num(c.closed) + num(c.reopened) + num(c.unknown) === 0) return '';
  return run.kind === RUN_KIND_REOPEN ? REOPEN_SETTLE_NOTE : CLOSE_SETTLE_NOTE;
};

export const RUNS_LOAD_FAILED_TEXT =
  'Could not check whether a close or reopen run is in progress.';

// B8/CD-91/CD-108: hard blockers, no month row and not-confirmed teachers are never re-picked.
export const NO_RETRY_TEXT =
  'None of these teachers can be retried as they are: each has a blocker a new run cannot clear, or its outcome was not confirmed. Fix them from their issues, then start a new run.';

export const RUN_LOAD_FAILED_TEXT = 'Could not load the month run.';
